import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Calendar, Star, Waves } from "lucide-react";
import { TID } from "@/constants/testIds";

const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
  } catch {
    return "";
  }
};

export const EventCard = ({ event, index = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.6, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
    data-testid={TID.eventCard(event.id)}
    className="card-lift bg-[color:var(--shell)] rounded-[1.75rem] overflow-hidden border border-[color:var(--line)]"
  >
    <Link to={`/events/${event.slug}`} className="block">
      <div className="relative aspect-[4/3] overflow-hidden">
        <img src={event.image} alt={event.title} className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute top-3 left-3 chip !bg-white/90 backdrop-blur">{event.category}</div>
        {event.featured && (
          <div className="absolute top-3 right-3 chip !bg-[color:var(--coral)] !text-white !border-[color:var(--coral)]">Hot pick</div>
        )}
      </div>
      <div className="p-6">
        <div className="flex items-center gap-3 text-xs text-[color:var(--ink-2)] mb-3 font-semibold">
          <span className="inline-flex items-center gap-1"><Calendar size={12} /> {fmtDate(event.start_date)}</span>
          <span className="inline-flex items-center gap-1"><MapPin size={12} /> {event.suburb}</span>
        </div>
        <h3 className="font-display text-[1.4rem] leading-tight text-[color:var(--ocean)] mb-2">{event.title}</h3>
        <p className="text-sm text-[color:var(--ink-2)] leading-relaxed mb-4 line-clamp-2">{event.short_description}</p>
        <div className="flex items-center justify-between pt-3 border-t border-dashed border-[color:var(--line)]">
          <span className="text-sm font-semibold text-[color:var(--ocean)]">
            {event.price_mode === "see_link" ? <span className="text-xs" data-testid="event-price-see-link">See link for ticket price</span> : event.price_mode === "free" || event.price_from === 0 ? "Free" : <><span className="opacity-60 font-normal text-xs">from </span>AUD ${event.price_from}</>}
          </span>
          <span className="link-editorial text-xs">Grab tickets →</span>
        </div>
      </div>
    </Link>
  </motion.div>
);

export const ExperienceCard = ({ exp, index = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.6, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
    data-testid={TID.expCard(exp.id)}
    className="card-lift bg-[color:var(--shell)] rounded-[1.75rem] overflow-hidden border border-[color:var(--line)]"
  >
    <Link to={`/experiences/${exp.slug}`} className="block">
      <div className="relative aspect-[4/5] overflow-hidden">
        <img src={exp.image} alt={exp.title} className="w-full h-full object-cover" loading="lazy" />
        <div className="absolute inset-0 hero-scrim" />
        <div className="absolute bottom-4 left-4 right-4 text-white">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[0.72rem] font-bold uppercase tracking-[0.14em] opacity-95">{exp.category.replace("-", " ")}</span>
            <span className="inline-flex items-center gap-1 text-xs bg-[color:var(--mango)] text-[color:var(--ink)] font-bold px-2 py-0.5 rounded-full">
              <Star size={11} className="fill-current" /> {exp.rating}
            </span>
          </div>
          <h3 className="font-display text-[1.45rem] leading-tight">{exp.title}</h3>
        </div>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-3 mb-3">
          <img src={exp.host.avatar} alt={exp.host.name} className="w-10 h-10 rounded-full object-cover border-2 border-[color:var(--mango)]" />
          <div>
            <div className="text-[0.7rem] uppercase tracking-wider text-[color:var(--ink-2)] font-bold">Hosted by</div>
            <div className="text-sm font-bold text-[color:var(--ocean)]">{exp.host.name}</div>
          </div>
        </div>
        <div className="flex items-center justify-between pt-3 border-t border-dashed border-[color:var(--line)]">
          <span className="text-xs text-[color:var(--ink-2)] font-semibold">{exp.duration_hours}h · up to {exp.max_guests}</span>
          <span className="text-sm font-bold text-[color:var(--ocean)]">
            <span className="opacity-60 font-normal text-xs">from </span>AUD ${exp.price}
          </span>
        </div>
      </div>
    </Link>
  </motion.div>
);

export const BusinessCard = ({ biz, index = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.5, delay: index * 0.04 }}
    data-testid={TID.bizCard(biz.id)}
    className="card-lift bg-[color:var(--shell)] rounded-[1.75rem] overflow-hidden border border-[color:var(--line)]"
  >
    <Link to={`/directory/${biz.slug}`} className="flex flex-col md:flex-row">
      <div className="md:w-56 h-48 md:h-auto flex-shrink-0 overflow-hidden">
        <img src={biz.image} alt={biz.name} className="w-full h-full object-cover" loading="lazy" />
      </div>
      <div className="p-6 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-[0.7rem] uppercase tracking-[0.14em] text-[color:var(--coral)] font-bold mb-2">{biz.category}</div>
            <h3 className="font-display text-2xl leading-tight text-[color:var(--ocean)]">{biz.name}</h3>
          </div>
          <span className="chip">{biz.price_range}</span>
        </div>
        <p className="text-sm text-[color:var(--ink-2)] leading-relaxed my-3 line-clamp-2">{biz.description}</p>
        <div className="flex items-center gap-4 text-xs text-[color:var(--ink-2)] font-semibold">
          <span className="inline-flex items-center gap-1"><MapPin size={12} /> {biz.suburb}</span>
          <span className="inline-flex items-center gap-1"><Star size={12} className="fill-[color:var(--mango)] text-[color:var(--mango)]" /> {biz.rating} <span className="font-normal opacity-70">({biz.review_count})</span></span>
        </div>
      </div>
    </Link>
  </motion.div>
);

export const GuideCard = ({ guide, size = "md", index = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{ duration: 0.6, delay: index * 0.05, ease: [0.16, 1, 0.3, 1] }}
    data-testid={TID.guideCard(guide.id)}
    className="card-lift"
  >
    <Link to={`/guides/${guide.slug}`} className="block group">
      <div className={`relative overflow-hidden rounded-[1.75rem] ${size === "lg" ? "aspect-[3/4]" : "aspect-[4/3]"}`}>
        <img src={guide.cover_image} alt={guide.title} className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" loading="lazy" />
        <div className="absolute inset-0 hero-scrim" />
        <div className="absolute top-4 left-4 chip !bg-white/90 backdrop-blur">{guide.category.replace("-", " ")}</div>
        {size === "lg" && (
          <div className="absolute bottom-6 left-6 right-6 text-white">
            <h3 className="font-display text-3xl md:text-4xl leading-tight">{guide.title}</h3>
            {guide.subtitle && <p className="mt-2 font-hand text-xl text-[color:var(--mango)]">{guide.subtitle}</p>}
          </div>
        )}
      </div>
      {size !== "lg" && (
        <div className="mt-5">
          <h3 className="font-display text-xl leading-tight text-[color:var(--ocean)]">{guide.title}</h3>
          <div className="mt-3 flex items-center gap-3 text-xs text-[color:var(--ink-2)] font-semibold">
            <span>{guide.author}</span>
            <Waves size={10} className="opacity-40" />
            <span>{guide.read_time_min} min read</span>
          </div>
        </div>
      )}
    </Link>
  </motion.div>
);
